/**
 * Datele clientului care se completeaza in CRM (contact, IBAN, observatii, felul platii) — mutat
 * din vechea lista „Clienții mei" in fisa clientului (20.10.2026). ANAF si ERP raman doar citire.
 */
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api } from '../../lib/api'
import { Button } from '../ui/Button'

const errMsg = (e: unknown) =>
  (e as { response?: { data?: { error?: { message?: string } } } }).response?.data?.error
    ?.message || 'Eroare necunoscută'

interface ClientDetail {
  id: number
  name: string
  cif: string | null
  regCom: string | null
  address: string | null
  city: string | null
  county: string | null
  postcode: string | null
  agentName: string | null
  paymTermDays: number | null
  paymentMode: 'SCADENTA' | 'PE_MASURA_VANZARII'
  eaGrup: string | null
  eaSyncStatus: string
  legalRep: string | null
  contactPerson: string | null
  contactEmail: string | null
  phone: string | null
  iban: string | null
  swift: string | null
  remarks: string | null
  target2026: string | null
}

type EditableKey = 'contactPerson' | 'contactEmail' | 'phone' | 'iban' | 'swift' | 'legalRep'

const EDITABLE_LABELS: Record<EditableKey, string> = {
  contactPerson: 'Persoană de contact',
  contactEmail: 'Email contact',
  phone: 'Telefon',
  iban: 'IBAN',
  swift: 'SWIFT',
  legalRep: 'Reprezentant legal',
}

export function ClientEditModal({ clientId, onClose }: { clientId: number; onClose: () => void }) {
  const queryClient = useQueryClient()
  const [form, setForm] = useState<Record<EditableKey, string>>({
    contactPerson: '',
    contactEmail: '',
    phone: '',
    iban: '',
    swift: '',
    legalRep: '',
  })
  const [remarks, setRemarks] = useState('')
  const [agentName, setAgentName] = useState('')
  const [paymentMode, setPaymentMode] = useState<'SCADENTA' | 'PE_MASURA_VANZARII'>('SCADENTA')
  const [loaded, setLoaded] = useState(false)

  const agentsQ = useQuery<{ agents: { agentName: string; userName: string }[] }>({
    queryKey: ['orders', 'agents'],
    queryFn: async () => (await api.get('/orders/agents')).data,
  })

  const q = useQuery<{ client: ClientDetail }>({
    queryKey: ['orders', 'client', clientId],
    queryFn: async () => (await api.get(`/orders/clients/${clientId}`)).data,
  })
  const c = q.data?.client
  const pending = c?.eaSyncStatus === 'pending'

  if (c && !loaded) {
    setLoaded(true)
    setForm({
      contactPerson: c.contactPerson ?? '',
      contactEmail: c.contactEmail ?? '',
      phone: c.phone ?? '',
      iban: c.iban ?? '',
      swift: c.swift ?? '',
      legalRep: c.legalRep ?? '',
    })
    setRemarks(c.remarks ?? '')
    setAgentName(c.agentName ?? '')
    setPaymentMode(c.paymentMode === 'PE_MASURA_VANZARII' ? 'PE_MASURA_VANZARII' : 'SCADENTA')
  }

  const save = useMutation({
    mutationFn: async () =>
      (
        await api.patch(`/orders/clients/${clientId}`, {
          ...form,
          remarks,
          paymentMode,
          // Agentul se poate seta doar cat timp clientul nu e inca in EA.
          ...(pending ? { agentName: agentName || null } : {}),
        })
      ).data,
    onSuccess: () => {
      // Lista si fisa (19.10: cheia veche `['agents','my-portfolio']` nu reimprospata nimic).
      void queryClient.invalidateQueries({ queryKey: ['clienti'] })
      queryClient.invalidateQueries({ queryKey: ['orders', 'client', clientId] })
      toast.success('Client actualizat')
      onClose()
    },
    onError: (e) => toast.error(errMsg(e)),
  })

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4">
      <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-white shadow-lg">
        <div className="border-b border-b4l-border px-5 py-3">
          <h2 className="text-lg font-semibold">{c ? c.name : 'Client'}</h2>
        </div>

        <div className="space-y-4 px-5 py-4">
          {q.isLoading || !c ? (
            <p className="text-sm text-b4l-text-muted">Se încarcă...</p>
          ) : (
            <>
              <div>
                <div className="mb-1 text-xs font-medium text-b4l-text-muted">
                  Date oficiale (din ANAF) — nu se editează aici
                </div>
                <div className="grid grid-cols-2 gap-2 rounded border border-b4l-border bg-b4l-bg p-3 text-xs sm:grid-cols-3">
                  <ReadOnly label="Denumire" value={c.name} />
                  <ReadOnly label="CIF" value={c.cif} />
                  <ReadOnly label="Nr. Reg. Com." value={c.regCom} />
                  <ReadOnly label="Adresă" value={c.address} />
                  <ReadOnly label="Oraș" value={c.city} />
                  <ReadOnly label="Județ" value={c.county} />
                </div>
              </div>

              <div>
                <div className="mb-1 text-xs font-medium text-b4l-text-muted">
                  Date comerciale (din ExpertAccounts) — se modifică în ExpertAccounts
                </div>
                <div className="grid grid-cols-1 gap-2 rounded border border-b4l-border bg-b4l-bg p-3 text-xs sm:grid-cols-3">
                  {pending ? (
                    <label className="block">
                      <span className="mb-0.5 block text-b4l-text-muted">Agent</span>
                      <select
                        value={agentName}
                        onChange={(e) => setAgentName(e.target.value)}
                        className="w-full rounded border border-b4l-border bg-white px-1.5 py-1 text-xs"
                      >
                        <option value="">— nealocat —</option>
                        {(agentsQ.data?.agents ?? []).map((a) => (
                          <option key={a.agentName} value={a.agentName}>
                            {a.userName}
                          </option>
                        ))}
                      </select>
                    </label>
                  ) : (
                    <ReadOnly label="Agent" value={c.agentName} />
                  )}
                  <ReadOnly
                    label="Termen plată"
                    value={c.paymTermDays != null ? `${c.paymTermDays} zile` : null}
                  />
                  <ReadOnly label="Grup" value={c.eaGrup} />
                </div>
                {pending && (
                  <p className="mt-1 text-[11px] text-b4l-amber-dark">
                    Clientul nu e încă în ExpertAccounts. Se retrimite automat din oră în oră (cel
                    târziu odată cu prima comandă). Până atunci, agentul se poate schimba de aici;
                    după sincronizare, doar din ExpertAccounts.
                  </p>
                )}
              </div>

              <div>
                <div className="mb-1 text-xs font-medium text-b4l-text-muted">
                  Date de contact (editabile)
                </div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {(Object.keys(EDITABLE_LABELS) as EditableKey[]).map((k) => (
                    <label key={k} className="block">
                      <span className="mb-0.5 block text-xs text-b4l-text-muted">
                        {EDITABLE_LABELS[k]}
                      </span>
                      <input
                        value={form[k]}
                        onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))}
                        className="w-full rounded border border-b4l-border px-2 py-1.5 text-sm"
                      />
                    </label>
                  ))}
                </div>
                {/* Modul de plata NU vine din ExpertAccounts — acolo notiunea nu exista.
                    Un termen de plata gol in EA inseamna doar ca nu l-a completat nimeni, deci
                    nu se poate deduce. Se marcheaza aici, de om. */}
                <label className="mt-3 block">
                  <span className="mb-0.5 block text-xs text-b4l-text-muted">Mod de plată</span>
                  <select
                    value={paymentMode}
                    onChange={(e) =>
                      setPaymentMode(e.target.value as 'SCADENTA' | 'PE_MASURA_VANZARII')
                    }
                    className="w-full rounded border border-b4l-border px-2 py-1.5 text-sm"
                  >
                    <option value="SCADENTA">La scadență — termen pe factură</option>
                    <option value="PE_MASURA_VANZARII">
                      Pe măsura vânzării — plătește pe măsură ce vinde marfa
                    </option>
                  </select>
                  <span className="mt-0.5 block text-[11px] text-b4l-text-muted">
                    Decide ce se arată în coloana {'„Scadent"'} din Tabloul de bord vânzări.
                  </span>
                </label>
                <label className="mt-3 block">
                  <span className="mb-0.5 block text-xs text-b4l-text-muted">
                    Observații partener{' '}
                    <span className="text-[11px]">(ajung pe fișa din ExpertAccounts)</span>
                  </span>
                  <textarea
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    rows={2}
                    className="w-full rounded border border-b4l-border px-2 py-1.5 text-sm"
                  />
                </label>
              </div>
            </>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-b4l-border px-5 py-3">
          <Button variant="secondary" onClick={onClose}>
            Închide
          </Button>
          <Button onClick={() => save.mutate()} loading={save.isPending} disabled={!c}>
            Salvează
          </Button>
        </div>
      </div>
    </div>
  )
}

function ReadOnly({ label, value }: { label: string; value: string | null }) {
  return (
    <div>
      <div className="text-b4l-text-muted">{label}</div>
      <div className="font-medium">{value || '—'}</div>
    </div>
  )
}
